import type { CardDef, Outcome, SceneDef, SoloRunState, StoryPack, RunScope, Track } from '../types.js';
import { STARTER_DECK_IDS, allCardDefs, getCardById } from '../cards/pool.js';
import { applyEffects } from './apply-effects.js';
import { resolveOutcome, cardBeatCompatible } from '../outcomes/resolve.js';
import type { LlmOutcomeClient } from '../llm/fallback.js';
import {
  getRunBounds,
  isChapterEnd,
  isLastSceneInRun,
  nextChapterAfter,
} from '../story/chapters.js';
import { describePlay, registerSceneCast, sceneLogEntry, planOutcome } from '../story/consequence.js';
import { cardSwing, endingTarget } from './balance.js';

const MOODS = new Set(['calm', 'tense', 'chaos', 'triumph', 'dread']);

function shuffle<T>(arr: T[], rng: () => number = Math.random): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export interface EngineConfig {
  story: StoryPack;
  scarCardIds: string[];
  rng?: () => number;
  llm?: LlmOutcomeClient;
}

export interface RunStartOptions {
  scope?: RunScope;
  chapterId?: string;
  /** Resume at this scene (continue flow) */
  sceneIndex?: number;
  /** Carry state from a saved continue */
  carry?: Partial<SoloRunState>;
}

export function createInitialRunState(
  chosenEndingId: string,
  config: EngineConfig,
  options: RunStartOptions = {},
): SoloRunState {
  const rng = config.rng ?? Math.random;
  const scope = options.scope ?? 'full';
  const bounds = getRunBounds(config.story, scope, options.chapterId);
  const deck = options.carry?.deck ? [...options.carry.deck] : [...STARTER_DECK_IDS];
  if (!options.carry?.deck) {
    for (const scarId of config.scarCardIds) {
      if (!deck.includes(scarId)) deck.push(scarId);
    }
  }
  const shuffled = options.carry?.drawPile ? [] : shuffle(deck, rng);
  const hand = options.carry?.hand ?? shuffled.slice(0, 4);
  const drawPile = options.carry?.drawPile ?? shuffled.slice(4);
  const startIndex = options.sceneIndex ?? bounds.startSceneIndex;
  const firstChapter = config.story.chapters?.find((c) => c.sceneStart === startIndex);

  return {
    phase: firstChapter && startIndex === firstChapter.sceneStart ? 'chapter_intro' : 'scene_intro',
    runScope: scope,
    chapterId: scope === 'chapter' ? options.chapterId ?? null : null,
    runStartSceneIndex: bounds.startSceneIndex,
    runEndSceneIndex: bounds.endSceneIndex,
    pendingChapterId: firstChapter?.id ?? null,
    sceneIndex: startIndex,
    energy: 3,
    maxEnergy: 3,
    dread: options.carry?.dread ?? 0,
    tracks: options.carry?.tracks ?? { hope: 0, dread: 0, chaos: 0, fate: 0 },
    variables: { ...(config.story.variables ?? {}), ...(options.carry?.variables ?? {}) },
    deck,
    hand,
    discard: options.carry?.discard ?? [],
    drawPile,
    callbacks: options.carry?.callbacks ?? {},
    keepsakes: options.carry?.keepsakes ?? {},
    playedThisScene: [],
    energySpentThisScene: 0,
    chosenEndingId,
    endingReached: null,
    sceneMood: 'calm',
    lastOutcome: null,
    draftOptions: [],
    upgradeTargetId: null,
    narratorGlitchActive: false,
    history: options.carry?.history ?? [],
    sceneLog: options.carry?.sceneLog ?? [],
    metCast: options.carry?.metCast ?? [],
  };
}

export function getCurrentScene(story: StoryPack, state: SoloRunState): SceneDef {
  return story.scenes[state.sceneIndex] ?? story.scenes[story.scenes.length - 1];
}

export function fillNarratorText(
  text: string,
  variables: Record<string, string | number | boolean>,
): string {
  let out = text;
  for (const [k, v] of Object.entries(variables)) {
    out = out.replaceAll(`{${k}}`, String(v));
  }
  return out;
}

export function canPlayCard(
  state: SoloRunState,
  cardId: string,
  beatId: string,
  story: StoryPack,
  extraCards: CardDef[] = [],
): { ok: boolean; reason?: string } {
  if (state.phase !== 'scene_play') return { ok: false, reason: 'wrong_phase' };
  if (!state.hand.includes(cardId)) return { ok: false, reason: 'not_in_hand' };
  const card = getCardById(cardId, extraCards);
  if (!card) return { ok: false, reason: 'unknown_card' };
  if (card.cost > state.energy) return { ok: false, reason: 'no_energy' };
  const scene = getCurrentScene(story, state);
  const beat = scene.beats.find((b) => b.id === beatId);
  if (!beat) return { ok: false, reason: 'unknown_beat' };
  if (!cardBeatCompatible(card, beat)) return { ok: false, reason: 'beat_mismatch' };
  return { ok: true };
}

export function playCard(
  state: SoloRunState,
  cardId: string,
  beatId: string,
  config: EngineConfig,
): SoloRunState {
  const check = canPlayCard(state, cardId, beatId, config.story);
  if (!check.ok) return state;

  const scene = getCurrentScene(config.story, state);
  const beat = scene.beats.find((b) => b.id === beatId)!;
  const card = getCardById(cardId)!;
  const element = scene.elements.find((e) => e.id === beat.elementId) ?? scene.elements[0];

  const { outcome } = resolveOutcome(
    {
      story: config.story,
      sceneId: scene.id,
      beat,
      card,
      element,
      state,
    },
    config.llm,
  );

  let next = applyEffects(state, outcome.effects);
  next.energy -= card.cost;
  next.energySpentThisScene += card.cost;
  next.hand = next.hand.filter((id) => id !== cardId);
  next.discard = [...next.discard, cardId];
  // The card type's own risk or reward rides on top of the outcome (see balance.ts).
  const swing = cardSwing(card, outcome.effects, config.rng ?? Math.random);
  if (swing?.effects.length) {
    const e = next.energy;
    next = applyEffects(next, swing.effects);
    next.tracks = Object.fromEntries(Object.entries(next.tracks).map(([k, v]) => [k, Math.max(0, v)])) as typeof next.tracks;
    if (swing.effects.some((x) => x.type === 'grant_energy')) next.energy = Math.min(next.maxEnergy, e + 1);
  }
  next.playedThisScene = [
    ...next.playedThisScene,
    { cardId, beatId, sceneId: scene.id, outcomeKey: outcome.key },
  ];
  next.lastOutcome = outcome;
  next.narratorGlitchActive = true;
  // Every outcome visibly repaints the scene: use its mood when it did not set one explicitly.
  if (!outcome.effects.some((e) => e.type === 'scene_mood') && MOODS.has(outcome.visual.mood)) {
    next.sceneMood = outcome.visual.mood as SoloRunState['sceneMood'];
  }

  // Blocking the narrator's plan eases Dread once per scene. An unopposed plan costs Dread at
  // scene end instead (see planOutcome), so playing cards is never worse than sitting still.
  const wasBlocked = Boolean(state.variables.intent_blocked) || state.playedThisScene.some((p) => p.cardId.startsWith('counter'));
  const nowBlocked = Boolean(next.variables.intent_blocked) || card.type === 'counter';
  if (nowBlocked && !wasBlocked) {
    next.dread = Math.max(0, next.dread - 1);
  }

  const change = describePlay({ prev: state, next, card, element, scene, story: config.story, swing });
  next.history = [...(state.history ?? []), change];

  return next;
}

export function advanceChapterIntro(state: SoloRunState): SoloRunState {
  if (state.phase !== 'chapter_intro') return state;
  return {
    ...state,
    phase: 'scene_intro',
    pendingChapterId: null,
    narratorGlitchActive: false,
  };
}

export function advanceSceneIntro(state: SoloRunState): SoloRunState {
  if (state.phase !== 'scene_intro') return state;
  const variables = { ...state.variables };
  delete variables.intent_blocked;
  return {
    ...state,
    phase: 'scene_play',
    energy: state.maxEnergy,
    energySpentThisScene: 0,
    playedThisScene: [],
    narratorGlitchActive: false,
    lastOutcome: null,
    variables,
    // each scene opens fresh, tinted by how much Dread has piled up
    sceneMood: state.dread >= 7 ? 'dread' : state.dread >= 4 ? 'tense' : 'calm',
    sceneStart: { dread: state.dread, tracks: { ...state.tracks } },
  };
}

export function endScenePlay(state: SoloRunState, config: EngineConfig): SoloRunState {
  if (state.phase !== 'scene_play') return state;

  const drawCount = Math.max(0, 4 - state.hand.length);
  let hand = [...state.hand];
  let drawPile = [...state.drawPile];
  let discard = [...state.discard];

  const rng = config.rng ?? Math.random;
  for (let i = 0; i < drawCount; i++) {
    if (drawPile.length === 0) {
      if (discard.length === 0) break;
      drawPile = shuffle(discard, rng);
      discard = [];
    }
    const card = drawPile.pop();
    if (card) hand.push(card);
  }

  const bounds = {
    startSceneIndex: state.runStartSceneIndex,
    endSceneIndex: state.runEndSceneIndex,
  };
  const isLast = isLastSceneInRun(config.story, state.sceneIndex, bounds);
  // The narrator's plan resolves: unopposed plans push Dread toward the Archivist's ending.
  const plan = planOutcome(state);
  const scene = getCurrentScene(config.story, state);
  state = {
    ...state,
    dread: Math.max(0, Math.min(10, state.dread + plan.dread)),
    metCast: registerSceneCast(state.metCast, scene),
  };
  const sceneLog = [...(state.sceneLog ?? []), sceneLogEntry(state, scene)];

  if (isLast) {
    const ending = evaluateEnding(state, config.story);
    return {
      ...state,
      hand,
      drawPile,
      discard,
      phase: 'run_end',
      endingReached: ending,
      sceneLog,
    };
  }

  const draftOptions = pickDraftOptions(state, config, rng);
  const atChapterEnd = isChapterEnd(config.story, state.sceneIndex);
  const nxt = nextChapterAfter(config.story, state.sceneIndex);

  return {
    ...state,
    hand,
    drawPile,
    discard,
    phase: 'draft',
    sceneLog,
    draftOptions,
    narratorGlitchActive: false,
    pendingChapterId:
      state.runScope === 'full' && atChapterEnd && nxt ? nxt.id : state.pendingChapterId,
  };
}

function pickDraftOptions(state: SoloRunState, config: EngineConfig, rng: () => number): string[] {
  const pool = allCardDefs()
    .filter((c) => !c.isScar && !state.deck.includes(c.id))
    .map((c) => c.id);
  const shuffled = shuffle(pool, rng);
  return shuffled.slice(0, 3);
}

export type DraftChoice = 'take' | 'upgrade' | 'burn' | 'skip';

export function applyDraftChoice(
  state: SoloRunState,
  choice: DraftChoice,
  cardIdOrTarget?: string,
  config?: EngineConfig,
): SoloRunState {
  if (state.phase !== 'draft') return state;

  let deck = [...state.deck];
  let hand = [...state.hand];
  let discard = [...state.discard];

  if (choice === 'skip') {
    // Keep deck as-is and move on.
  } else if (choice === 'take' && cardIdOrTarget) {
    deck.push(cardIdOrTarget);
    hand.push(cardIdOrTarget);
  } else if (choice === 'upgrade' && cardIdOrTarget) {
    const card = getCardById(cardIdOrTarget);
    if (card?.upgradeId) {
      deck = deck.map((id) => (id === cardIdOrTarget ? card.upgradeId! : id));
      hand = hand.map((id) => (id === cardIdOrTarget ? card.upgradeId! : id));
    }
  } else if (choice === 'burn' && cardIdOrTarget) {
    deck = deck.filter((id) => id !== cardIdOrTarget);
    hand = hand.filter((id) => id !== cardIdOrTarget);
    discard.push(cardIdOrTarget);
  }

  const nextSceneIndex = state.sceneIndex + 1;
  const ch = config?.story.chapters?.find((c) => c.id === state.pendingChapterId);
  const phase: SoloRunState['phase'] =
    state.pendingChapterId && ch && nextSceneIndex === ch.sceneStart
      ? 'chapter_intro'
      : 'scene_intro';

  const next: SoloRunState = {
    ...state,
    deck,
    hand,
    discard,
    phase,
    draftOptions: [],
    sceneIndex: nextSceneIndex,
  };

  if (config && state.dread >= 10) {
    return { ...next, phase: 'run_end', endingReached: 'ending_dread' };
  }

  return next;
}

export { getSceneBackgroundId } from '../story/scene-utils.js';

export function evaluateEnding(state: SoloRunState, story: StoryPack): string {
  if (state.dread >= 10) return 'ending_dread';
  const chosen = story.endings.find((e) => e.id === state.chosenEndingId);
  if (chosen) {
    const trackVal = state.tracks[chosen.track] ?? 0;
    if (trackVal >= endingTarget(chosen, state)) return chosen.id;
  }
  // Missing the chosen target never awards it anyway: another ending decides. First any other ending
  // whose own target was reached (best margin), else the strongest meter outside the chosen one's track.
  const others = story.endings.filter((e) => e.id !== chosen?.id && e.track !== 'dread');
  const reached = others
    .map((e) => ({ e, margin: (state.tracks[e.track] ?? 0) - endingTarget(e, state) }))
    .filter((x) => x.margin >= 0)
    .sort((a, b) => b.margin - a.margin);
  if (reached[0]) return reached[0].e.id;
  const sorted = (Object.entries(state.tracks) as [Track, number][])
    .filter(([t]) => t !== 'dread' && t !== chosen?.track)
    .sort((a, b) => b[1] - a[1]);
  const top = sorted[0]?.[0];
  const fallback = story.endings
    .filter((e) => e.track === top)
    .sort((a, b) => a.minValue - b.minValue)[0];
  return fallback?.id ?? others[0]?.id ?? story.endings[0].id;
}

export function checkRunLoss(state: SoloRunState): boolean {
  return state.dread >= 10;
}

export function memoryLineForOutcome(outcome: Outcome, sceneTitle: string): string {
  return `Last time, in "${sceneTitle}", you played a card and the narrator said: "${outcome.narratorQuip}"`;
}
